import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import { Loader2, Sparkles } from "lucide-react";
import toast from "react-hot-toast";
import FieldEditModal from "./FieldEditModal";
import FieldNavigatorSidebar from "./FieldNavigatorSidebar";
import PageThumbnailsSidebar from "./PageThumbnailsSidebar";
import BboxOverlay from "./BboxOverlay";
import FormReviewTopBar from "./FormReviewTopBar";
import FormCanvasToolbar from "./FormCanvasToolbar";
import SignatureModal from "./SignatureModal";
import ConfirmDialog from "../../../../components/ConfirmDialog/ConfirmDialog";
import useAutoPdfJob from "./hooks/useAutoPdfJob";
import useAutoPdfMode from "./hooks/useAutoPdfMode";
import useManualAnnotate from "./hooks/useManualAnnotate";
import useDrawGesture from "./hooks/useDrawGesture";
import useBboxGesture from "./hooks/useBboxGesture";
import useDownloadJob from "./hooks/useDownloadJob";
import useFieldEditor from "./hooks/useFieldEditor";
import useFieldDelete from "./hooks/useFieldDelete";
import useFillFinalize from "./hooks/useFillFinalize";
import useFieldsView from "./hooks/useFieldsView";

pdfjs.GlobalWorkerOptions.workerSrc =
  "//cdnjs.cloudflare.com/ajax/libs/pdf.js/" +
  pdfjs.version +
  "/pdf.worker.min.js";

/**
 * Convert a backend bbox [x0, y0, x1, y1] to CSS-positioned values relative
 * to the rendered page.
 *
 * The autopdf backend emits bboxes in image-style coordinates (origin
 * top-left, y grows downward) — same convention as PyMuPDF/`fitz` and what
 * the backend itself uses to write the filled PDF. So we do NOT y-flip.
 */
const bboxToCss = (bbox, pdfWidth, pdfHeight, scale) => {
  const [x0, y0, x1, y1] = bbox;
  return {
    left: x0 * scale,
    top: y0 * scale,
    width: (x1 - x0) * scale,
    height: (y1 - y0) * scale,
  };
};

const FormReviewPage = () => {
  const { jobId, projectId, orgId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const initialPdfUrl = location.state?.pdfUrl || "";
  const initialName = location.state?.name || "Form";

  // Server state + PDF rendering bookkeeping (job, pdfUrl, totalPages, etc.)
  const {
    job,
    setJob,
    pdfUrl,
    setPdfUrl,
    loading,
    hasAnswers,
    setHasAnswers,
    isFinalizedView,
    setIsFinalizedView,
    totalPages,
    setTotalPages,
    pageDims,
    setPageDims,
    applyJobData,
    pollJobUntilFilled,
    pollJobUntilDetected,
  } = useAutoPdfJob(jobId, initialPdfUrl);

  // Refs are declared up here because several hooks below want them.
  const containerRef = useRef(null);
  const pageWrapperRefs = useRef({});

  // Modular fill mode: which tool the user picked (null = not yet chosen).
  const mode = job?.mode || null;
  // Manual mode uses the Acrobat-style annotate toolbar for ALL PDFs (Add
  // Field / Fill Form / View + click-to-add text). Native AcroForm filling is
  // disabled for now so the editing tools are always available.
  const isNativeManual = false;
  const isManualAnnotate = mode === "manual";
  // Field-overlay UI (bboxes, draw, edit) applies to both AI and manual modes.
  const showFieldUI = mode === "ai" || mode === "manual";

  // Mode selection + on-demand AI annotation.
  const { detecting, switchingMode, chooseMode, runDetect } = useAutoPdfMode({
    jobId,
    setJob,
    pollJobUntilDetected,
  });

  // Sidebar memos + page navigator state (search, hover, IO observer, etc.)
  const {
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    collapsedSections,
    toggleSection,
    hoveredFieldId,
    setHoveredFieldId,
    highlightedFieldId,
    fieldsByPage,
    sidebarSections,
    stats,
    pageStats,
    currentPage,
    handleFieldRowClick,
    goToPage,
    getFieldStatus,
  } = useFieldsView({ job, totalPages, pageWrapperRefs });

  // Generate / Finalize workflow + shared fillStage.
  const {
    fillStage,
    setFillStage,
    filling,
    setReadyToFinalize,
    handleGenerateAnswer,
    cancelGenerate,
  } = useFillFinalize({
    jobId,
    job,
    applyJobData,
    pollJobUntilFilled,
    setIsFinalizedView,
  });

  // Open straight into the editable editor — there's no "pick a mode" step.
  // AI is an opt-in assist; the job is silently put in editable (manual) state.
  useEffect(() => {
    if (!loading && job && !job.mode && !switchingMode) chooseMode("manual");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, job?.id, job?.mode, switchingMode]);

  // AI assist is two explicit steps: Detect fields, then Generate answers.
  // Dismissible "try AI" assist banner + post-fill review summary.
  const [assistDismissed, setAssistDismissed] = useState(false);
  const [autoFillDone, setAutoFillDone] = useState(false);
  // Field ids currently being generated (current page) — drives the progress count.
  const [genScope, setGenScope] = useState(null);

  const handleDetect = async () => {
    if (detecting || filling) return;
    setAutoFillDone(false);
    setAssistDismissed(true);
    setGenScope(null);
    await runDetect();
  };

  // The page most visible in the scroll container right now (deterministic at
  // click time — the IntersectionObserver's currentPage can lag/be stale).
  const getVisiblePageIndex = () => {
    const cont = containerRef.current;
    if (!cont) return currentPage;
    const cr = cont.getBoundingClientRect();
    let best = currentPage;
    let bestArea = -1;
    for (let i = 0; i < totalPages; i++) {
      const el = pageWrapperRefs.current[i];
      if (!el) continue;
      const r = el.getBoundingClientRect();
      const visible = Math.max(0, Math.min(r.bottom, cr.bottom) - Math.max(r.top, cr.top));
      if (visible > bestArea) {
        bestArea = visible;
        best = i;
      }
    }
    return best;
  };

  const handleGenerate = async () => {
    if (detecting || filling) return;
    // Only generate answers for fields on the page the user is currently viewing.
    const visiblePage = getVisiblePageIndex();
    const pageFieldIds = (fieldsByPage[visiblePage] || []).map((f) => f.id);
    if (!pageFieldIds.length) {
      toast("No fields on this page to generate.", { icon: "ℹ️" });
      return;
    }
    setAutoFillDone(false);
    setGenScope(pageFieldIds); // kept after, so the summary reflects this page
    await handleGenerateAnswer(pageFieldIds);
    setAutoFillDone(true);
  };

  // Progress within the page being generated (falls back to overall stats).
  const genProgress = useMemo(() => {
    const ids = genScope;
    const all = job?.fields || [];
    const scoped = ids && ids.length ? all.filter((f) => ids.includes(f.id)) : all;
    const total = ids && ids.length ? ids.length : scoped.length;
    const done = scoped.filter(
      (f) => f.answer != null && f.answer.toString().trim() !== ""
    ).length;
    const review = scoped.filter((f) => f.needs_review).length;
    return { done, total, review };
  }, [genScope, job]);

  const hasFields = (job?.fields || []).length > 0;
  const autoFilling = detecting || filling;

  // Leaving the page (navigate away / unmount) while generating counts as a
  // cancel — the backend halts and re-generating later fills only the rest.
  const fillingRef = useRef(false);
  useEffect(() => {
    fillingRef.current = filling;
  }, [filling]);
  useEffect(
    () => () => {
      if (fillingRef.current) cancelGenerate();
    },
    [cancelGenerate]
  );

  // Friendly, human name for a field (used in dialogs/editors) instead of the
  // raw id like "manual_cc76052f": its label, else "<Type> <N>".
  const prettyFieldName = (field) => {
    if (!field) return "this field";
    if (field.label && field.label.trim()) return field.label.trim();
    const idx = (job?.fields || []).findIndex((f) => f.id === field.id);
    const typeName =
      field.field_type === "checkbox"
        ? "Checkbox"
        : field.field_type === "signature"
        ? "Signature"
        : field.field_type === "date"
        ? "Date"
        : "Field";
    return idx >= 0 ? `${typeName} ${idx + 1}` : typeName;
  };

  // Inline field editor (state + open/close + save PATCH).
  const {
    editingField,
    editValue,
    setEditValue,
    savingEdit,
    openFieldEditor,
    closeFieldEditor,
    handleSaveEdit,
    setEditingField,
  } = useFieldEditor({ jobId, job, setJob, setHasAnswers });

  // Manual editing — inline text edit for boxes; click-to-place + toggle for checks.
  const {
    editingId: manualEditingId,
    draft: manualDraft,
    setDraft: setManualDraft,
    startEdit: startManualEdit,
    cancelEdit: cancelManualEdit,
    createCheckAt: createManualCheck,
    createSignatureAt: createManualSignature,
    toggleCheck: toggleManualCheck,
    commitEdit: commitManualEdit,
  } = useManualAnnotate({ jobId, setJob, isFinalizedView });

  // "Check" stamp mode: clicking the PDF drops a tick.
  const [checkMode, setCheckMode] = useState(false);
  // "Sign" mode: a saved signature is being placed by clicking the PDF.
  const [signModalOpen, setSignModalOpen] = useState(false);
  const [signature, setSignature] = useState(null); // { dataUrl, aspect }
  const signMode = !!signature;

  // A field click routes to the right editor: checkboxes toggle, text boxes edit
  // inline (manual mode), and AI fields open the modal editor.
  const handleFieldActivate = (field) => {
    // Still saving (optimistic temp id) — wait for the server id before editing.
    if (String(field.id).startsWith("temp_")) return;
    if (isManualAnnotate) {
      if (field.field_type === "checkbox") return toggleManualCheck(field);
      // Signatures are images — move/resize/delete via handles, no text editor.
      if (field.field_type === "signature") return;
      return startManualEdit(field);
    }
    return openFieldEditor(field);
  };

  // Draw-to-create-field gesture (drag on a page → POST /fields/).
  const {
    drawMode,
    setDrawMode,
    drawing,
    creatingField,
    handlePageMouseDown,
  } = useDrawGesture({
    jobId,
    isFinalizedView,
    // Optimistic: show the box instantly, reconcile with the server in the bg.
    onOptimisticAdd: (optimistic) =>
      setJob((prev) =>
        prev ? { ...prev, fields: [...(prev.fields || []), optimistic] } : prev
      ),
    onCreated: (real, tmpId) => {
      setJob((prev) =>
        prev
          ? {
              ...prev,
              fields: (prev.fields || []).map((f) => (f.id === tmpId ? real : f)),
            }
          : prev
      );
      // Open the real field for editing once the server confirms it.
      handleFieldActivate(real);
    },
    onFailed: (tmpId) =>
      setJob((prev) =>
        prev
          ? { ...prev, fields: (prev.fields || []).filter((f) => f.id !== tmpId) }
          : prev
      ),
  });

  // Move/resize gesture for an existing field's bbox.
  const { bboxGesture, handleBboxMouseDown } = useBboxGesture({
    jobId,
    job,
    setJob,
    pageDims,
    drawMode,
    isFinalizedView,
    creatingField,
    onClickWithoutDrag: handleFieldActivate,
  });

  // Download button — auto-finalizes if needed, then GETs the blob.
  const { downloading, hasDownloaded, handleDownload, clearDownloaded } =
    useDownloadJob({
      jobId,
      job,
      isFinalizedView,
      applyJobData,
      setReadyToFinalize,
      setFillStage,
      manualMode: isManualAnnotate,
    });

  // Return from the finalized/downloaded read-only view back to editing:
  // restore the source PDF + editable overlays.
  const handleBackToEdit = () => {
    setIsFinalizedView(false);
    setReadyToFinalize(false);
    clearDownloaded();
    const source = job?.pdf_url || "";
    if (source && source !== pdfUrl) {
      setPageDims({});
      setTotalPages(0);
      setPdfUrl(source);
    }
  };

  // Field deletion (staged → confirm dialog → DELETE).
  const {
    pendingDeleteField,
    deletingField,
    requestDeleteField,
    confirmDeleteField,
    cancelDeleteField,
  } = useFieldDelete({
    jobId,
    setJob,
    editingFieldId: editingField?.id,
    onDeletedFieldOpenInEditor: () => {
      setEditingField(null);
      setEditValue("");
    },
  });


  // "fill" → editing affordances visible. "view" → read-only preview with
  // answers baked into the overlay positions.
  const [viewMode, setViewMode] = useState("fill");

  const [containerWidth, setContainerWidth] = useState(800);
  const [zoom] = useState(1);
  const [fieldsCollapsed, setFieldsCollapsed] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1280
  );

  const fileObj = useMemo(
    () => (pdfUrl ? { url: pdfUrl, withCredentials: false } : null),
    [pdfUrl]
  );

  // Responsive priority: collapse the fields panel when the window gets narrow
  // so the PDF canvas keeps room. Only fires on crossing the threshold, so the
  // user can still expand it manually while narrow.
  const narrow = windowWidth < 960;
  useEffect(() => {
    if (narrow) setFieldsCollapsed(true);
  }, [narrow]);

  // Resize observer (PDF column width + window width for responsive panels)
  useEffect(() => {
    const update = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
      setWindowWidth(window.innerWidth);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const handleBack = () => {
    if (orgId) {
      navigate(`/rfp-projects/project/${projectId}/org/${orgId}/forms`);
    } else {
      navigate(`/rfp-projects/project/${projectId}/forms`);
    }
  };


  return (
    <div className="h-full w-full flex flex-col overflow-hidden bg-[#FEF9F7] font-outfit">
      <FormReviewTopBar
        title={job?.name || initialName}
        statusLine={
          autoFilling
            ? "Working…"
            : isFinalizedView || hasDownloaded
            ? "Completed"
            : stats?.total
            ? `${stats.done} of ${stats.total} fields filled`
            : "Ready to fill"
        }
        onBack={handleBack}
        detecting={detecting}
        viewMode={viewMode}
        onSwitchToFill={() => setViewMode("fill")}
        onSwitchToView={() => { setViewMode("view"); setDrawMode(false); setCheckMode(false); setSignature(null); }}
        loading={loading}
        job={job}
        isFinalizedView={isFinalizedView}
        hasDownloaded={hasDownloaded}
        fillStage={fillStage}
        filling={filling}
        downloading={downloading}
        onDownload={handleDownload}
        onBackToEdit={handleBackToEdit}
      />

      {/* Body */}
      <div className="flex-1 min-h-0 flex">

        {/* Sidebar — field navigator (field-based modes only) */}
        {showFieldUI && (
          <FieldNavigatorSidebar
            loading={loading}
            job={job}
            stats={stats}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            sidebarSections={sidebarSections}
            collapsedSections={collapsedSections}
            toggleSection={toggleSection}
            getFieldStatus={getFieldStatus}
            highlightedFieldId={highlightedFieldId}
            hoveredFieldId={hoveredFieldId}
            setHoveredFieldId={setHoveredFieldId}
            isFinalizedView={isFinalizedView}
            onFieldRowClick={handleFieldRowClick}
            onOpenEditor={openFieldEditor}
            collapsed={fieldsCollapsed}
            onCollapse={() => setFieldsCollapsed(true)}
          />
        )}

        {/* PDF area — relative wrapper keeps the floating toolbar pinned */}
        <div className="flex-1 min-h-0 relative">
        {/* Auto-fill progress overlay — only during AI detect/generate, not the
            download's finalize step (which reuses the same fillStage). */}
        {autoFilling && !downloading && (
          <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
            <div className="w-[300px] bg-white rounded-xl shadow-xl border border-gray-200 px-6 py-5 text-center">
              <Loader2 className="w-7 h-7 animate-spin text-[#C95EBE] mx-auto" />
              <div className="mt-3 text-[14px] font-semibold text-[#18101C]">
                {detecting ? "Finding fields…" : "Generating answers…"}
              </div>
              {filling && genProgress.total > 0 && (
                <>
                  <div className="text-[12px] text-[#52525B] mt-1">
                    {genProgress.done} of {genProgress.total} filled
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-gray-100 mt-2 overflow-hidden">
                    <div
                      className="h-full bg-[#C95EBE] rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.round(
                          (genProgress.done / genProgress.total) * 100
                        )}%`,
                      }}
                    />
                  </div>
                </>
              )}
              <div className="text-[11px] text-[#A1A1AA] mt-2">
                Drafting from your knowledge base — this can take a moment.
              </div>
              {filling && (
                <button
                  type="button"
                  onClick={cancelGenerate}
                  className="mt-4 px-4 py-1.5 rounded-md text-[13px] font-medium border border-gray-200 text-[#52525B] hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        )}
        {/* Floating canvas toolbar — Select / Text / Check / Detect / Generate / Fields */}
        {!loading && pdfUrl && showFieldUI && !isFinalizedView && !hasDownloaded &&
          viewMode === "fill" && (
            <FormCanvasToolbar
              activeTool={
                signMode ? "sign" : checkMode ? "check" : drawMode ? "text" : "select"
              }
              onSelect={() => {
                setDrawMode(false);
                setCheckMode(false);
                setSignature(null);
              }}
              onAddText={() => {
                if (creatingField) return;
                setCheckMode(false);
                setSignature(null);
                setDrawMode((v) => !v);
              }}
              onAddCheck={() => {
                if (creatingField) return;
                setDrawMode(false);
                setSignature(null);
                setCheckMode((v) => !v);
              }}
              onSign={() => {
                setDrawMode(false);
                setCheckMode(false);
                // Re-open the create-signature modal (a saved one is replaced).
                setSignature(null);
                setSignModalOpen(true);
              }}
              onDetect={handleDetect}
              detecting={detecting}
              onGenerate={handleGenerate}
              generating={filling}
              canGenerate={hasFields}
              aiComplete={hasFields && stats.total > 0 && stats.done >= stats.total}
              onToggleFields={() => setFieldsCollapsed((v) => !v)}
              fieldsCollapsed={fieldsCollapsed}
              disabled={loading || !job}
            />
          )}
        <div className="absolute inset-0 overflow-auto p-4" ref={containerRef}>
        {loading ? (
          <div className="h-full w-full flex items-center justify-center text-[#52525B]">
            <Loader2 className="w-6 h-6 animate-spin text-[#C95EBE] mr-2" />
            Loading form...
          </div>
        ) : !pdfUrl ? (
          <div className="h-full flex flex-col items-center justify-center gap-3 text-[#52525B]">
            <p className="text-[14px]">We couldn't load this PDF.</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-lg bg-[#C95EBE] hover:bg-[#b04da6] text-white text-[13px] font-medium"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="mx-auto" style={{ maxWidth: 1100 }}>
            {/* Post-auto-fill review summary */}
            {autoFillDone && !autoFilling && !isFinalizedView && !hasDownloaded && (
              <div className="mb-3 flex items-center justify-between gap-3 text-[13px] bg-[#ECFDF3] border border-[#BBF7D0] rounded-lg py-2 px-3">
                <span className="text-[#166534]">
                  <span className="font-medium">
                    AI filled {genProgress.done} of {genProgress.total} fields.
                  </span>{" "}
                  {genProgress.review > 0
                    ? `${genProgress.review} need your review — they're marked amber. Check them, then Download.`
                    : "Review the answers and Download when ready."}
                </span>
                <button
                  type="button"
                  onClick={() => setAutoFillDone(false)}
                  className="px-2.5 py-1.5 rounded-md text-[13px] text-[#166534] hover:bg-white flex-shrink-0"
                >
                  Dismiss
                </button>
              </div>
            )}
            {isManualAnnotate && !isFinalizedView && !hasDownloaded && !autoFillDone &&
              !autoFilling &&
              (!hasFields && !assistDismissed ? (
                // No fields detected yet → offer the AI head-start.
                <div className="mb-3 flex items-center justify-between gap-3 text-[13px] bg-[#FDF2FB] border border-[#F3D2EC] rounded-lg py-2 px-3">
                  <span className="text-[#52525B]">
                    <span className="font-medium text-[#18101C]">Want a head start?</span>{" "}
                    Let AI detect the fields, then generate answers — you review before saving.
                  </span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      type="button"
                      onClick={handleDetect}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#C95EBE] hover:bg-[#b04da6] text-white text-[13px] font-medium transition-colors"
                    >
                      <Sparkles size={14} />
                      Detect fields
                    </button>
                    <button
                      type="button"
                      onClick={() => setAssistDismissed(true)}
                      className="px-2.5 py-1.5 rounded-md text-[13px] text-[#52525B] hover:bg-white"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mb-3 text-center text-[12px] text-[#52525B] bg-gray-50 border border-gray-200 rounded-md py-1.5 px-3">
                  Click a field to fill it · Generate answers runs per page — scroll
                  and run it on each page.
                </div>
              ))}
            <Document
              file={fileObj}
              onLoadSuccess={(pdf) => setTotalPages(pdf.numPages)}
              onLoadError={(err) => {
                console.error("PDF load error:", err);
                toast.error("Failed to load PDF.");
              }}
              loading={
                <div className="flex items-center justify-center py-10 text-[#52525B]">
                  <Loader2 className="w-5 h-5 animate-spin text-[#C95EBE] mr-2" />
                  Loading PDF...
                </div>
              }
            >
              {Array.from({ length: totalPages }).map((_, pageIndex) => {
                const dims = pageDims[pageIndex];
                const renderedWidth = Math.min(containerWidth - 32, 1100) * zoom;
                const scale = dims ? renderedWidth / dims.width : 1;
                const pageFields = fieldsByPage[pageIndex] || [];

                return (
                  <div
                    key={pageIndex}
                    style={{
                      position: "relative",
                      display: "flex",
                      justifyContent: "center",
                      marginBottom: 16,
                    }}
                  >
                    <div
                      ref={(el) => (pageWrapperRefs.current[pageIndex] = el)}
                      onMouseDown={(e) => {
                        if (!showFieldUI) return;
                        handlePageMouseDown(
                          e,
                          pageIndex,
                          pageWrapperRefs.current[pageIndex],
                          scale
                        );
                      }}
                      onClick={(e) => {
                        if (isFinalizedView) return;
                        if (e.target.closest("[data-field-overlay]")) return;
                        const el = pageWrapperRefs.current[pageIndex];
                        if (!el) return;
                        const rect = el.getBoundingClientRect();
                        const x = (e.clientX - rect.left) / scale;
                        const y = (e.clientY - rect.top) / scale;
                        // Sign mode: drop the saved signature image.
                        if (signMode) {
                          createManualSignature(
                            pageIndex,
                            x,
                            y,
                            signature.dataUrl,
                            signature.aspect
                          );
                          return;
                        }
                        // Check stamp: click anywhere to drop a tick.
                        if (checkMode) {
                          createManualCheck(pageIndex, x, y);
                        }
                      }}
                      style={{
                        position: "relative",
                        cursor:
                          (drawMode || checkMode || signMode) && showFieldUI
                            ? "crosshair"
                            : "default",
                        userSelect: drawMode && showFieldUI ? "none" : "auto",
                      }}
                    >
                      <Page
                        pageNumber={pageIndex + 1}
                        width={renderedWidth}
                        // Native AcroForm fill renders the interactive form
                        // layer so the user types straight into the widgets.
                        renderAnnotationLayer={isNativeManual}
                        renderForms={isNativeManual}
                        renderTextLayer={false}
                        onRenderSuccess={async (pageProxy) => {
                          const viewport = pageProxy.getViewport({ scale: 1 });
                          setPageDims((prev) => {
                            const existing = prev[pageIndex];
                            if (
                              existing &&
                              existing.width === viewport.width &&
                              existing.height === viewport.height
                            ) {
                              return prev;
                            }
                            return {
                              ...prev,
                              [pageIndex]: {
                                width: viewport.width,
                                height: viewport.height,
                              },
                            };
                          });
                        }}
                      />

                      {/* Field overlays — only in field-based modes (AI /
                          manual-flat), and hidden once the PDF is finalized
                          since the answers are baked into the rendered PDF. */}
                      {showFieldUI && dims && !isFinalizedView &&
                        pageFields.map((field) => {
                          // The field being inline-edited renders as a textarea
                          // (below) instead of a static overlay.
                          if (isManualAnnotate && manualEditingId === field.id)
                            return null;
                          // Use the in-progress gesture preview if this is
                          // the field currently being moved/resized.
                          const liveBbox =
                            bboxGesture && bboxGesture.fieldId === field.id
                              ? bboxGesture.currentBbox
                              : field.bbox;
                          const css = bboxToCss(
                            liveBbox,
                            dims.width,
                            dims.height,
                            scale
                          );
                          const answer = field.answer || "";
                          // Manual annotations always show their text; AI fields
                          // only once answers exist.
                          const showAnswer = isManualAnnotate
                            ? !!answer
                            : hasAnswers && answer;
                          const isOverridden = !!field.user_overridden;
                          const isViewMode = viewMode === "view";
                          const isActiveGesture =
                            bboxGesture && bboxGesture.fieldId === field.id;
                          const showAffordances =
                            !drawMode &&
                            !isViewMode &&
                            (hoveredFieldId === field.id || isActiveGesture);
                          return (
                            <BboxOverlay
                              key={field.id}
                              field={field}
                              css={css}
                              showAnswer={showAnswer}
                              isOverridden={isOverridden}
                              isViewMode={isViewMode}
                              isFinalizedView={isFinalizedView}
                              drawMode={drawMode}
                              showAffordances={showAffordances}
                              isHighlighted={highlightedFieldId === field.id}
                              scale={scale}
                              hasAnswers={hasAnswers}
                              onMouseEnter={() => setHoveredFieldId(field.id)}
                              onMouseLeave={() => setHoveredFieldId(null)}
                              onBboxMouseDown={handleBboxMouseDown}
                              onRequestDelete={requestDeleteField}
                              onActivate={handleFieldActivate}
                            />
                          );
                        })}

                      {/* Inline text editor for the manual annotation being
                          edited on this page. */}
                      {isManualAnnotate && dims && !isFinalizedView &&
                        pageFields
                          .filter((f) => f.id === manualEditingId)
                          .map((field) => {
                            const css = bboxToCss(
                              field.bbox,
                              dims.width,
                              dims.height,
                              scale
                            );
                            return (
                              <textarea
                                key={`edit-${field.id}`}
                                data-field-overlay="1"
                                aria-label={`Edit ${field.label || "field"} text`}
                                autoFocus
                                value={manualDraft}
                                onChange={(e) => setManualDraft(e.target.value)}
                                onBlur={commitManualEdit}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    commitManualEdit();
                                  } else if (e.key === "Escape") {
                                    e.preventDefault();
                                    cancelManualEdit();
                                  }
                                }}
                                style={{
                                  position: "absolute",
                                  left: css.left,
                                  top: css.top,
                                  width: Math.max(css.width, 90),
                                  minHeight: Math.max(css.height, 60),
                                  fontSize: Math.min(13, Math.max(9, css.height * 0.7)),
                                  lineHeight: 1.3,
                                  padding: "2px 4px",
                                  border: "1.5px solid #2563EB",
                                  borderRadius: 2,
                                  background: "rgba(255,255,255,0.98)",
                                  color: "#000",
                                  resize: "none",
                                  outline: "none",
                                  overflow: "auto",
                                  whiteSpace: "pre-wrap",
                                  fontFamily: "Outfit, sans-serif",
                                  zIndex: 10,
                                  boxShadow: "0 1px 6px rgba(0,0,0,0.18)",
                                }}
                              />
                            );
                          })}

                      {/* Live preview rectangle while the user is dragging
                          on this page. */}
                      {drawing && drawing.pageIndex === pageIndex && (
                        <div
                          style={{
                            position: "absolute",
                            left: Math.min(drawing.startX, drawing.currentX),
                            top: Math.min(drawing.startY, drawing.currentY),
                            width: Math.abs(
                              drawing.currentX - drawing.startX
                            ),
                            height: Math.abs(
                              drawing.currentY - drawing.startY
                            ),
                            border: "2px dashed #C95EBE",
                            background: "rgba(201, 94, 190, 0.15)",
                            pointerEvents: "none",
                          }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </Document>
          </div>
        )}
        </div>
        </div>

        {/* Page navigator (right) — hidden on narrow screens (responsive
            priority: Canvas > Fields > Thumbnails). */}
        {!loading && windowWidth >= 1200 && (
          <PageThumbnailsSidebar
            fileObj={fileObj}
            totalPages={totalPages}
            pageStats={pageStats}
            currentPage={currentPage}
            onGoToPage={goToPage}
          />
        )}
      </div>

      {/* Field Edit Modal */}
      {editingField && (
        <FieldEditModal
          field={editingField}
          value={editValue}
          onChange={setEditValue}
          onSave={handleSaveEdit}
          onCancel={closeFieldEditor}
          saving={savingEdit}
          displayName={prettyFieldName(editingField)}
        />
      )}

      {/* Create-signature modal → enter placement mode on save */}
      <SignatureModal
        open={signModalOpen}
        onClose={() => setSignModalOpen(false)}
        onSave={(dataUrl, aspect) => {
          setSignModalOpen(false);
          setSignature({ dataUrl, aspect: aspect || 0.35 });
        }}
      />

      {/* Delete Confirm Dialog */}
      {pendingDeleteField && (
        <ConfirmDialog
          title="Delete field?"
          message={
            <>
              You're about to delete{" "}
              <strong>{prettyFieldName(pendingDeleteField)}</strong>. This cannot
              be undone.
            </>
          }
          confirmLabel="Delete"
          cancelLabel="Cancel"
          danger
          busy={deletingField}
          onConfirm={confirmDeleteField}
          onCancel={cancelDeleteField}
        />
      )}
    </div>
  );
};


export default FormReviewPage;
